import { createContextApi, CACHE_POLICY, providesList } from '@quickbee/store';

// src/index.ts
var PRICING_TAGS = [
  "CurrentPrice",
  "PriceHistory",
  "ClosingPrice",
  "ManualOverride",
  "FeedConfig",
  "FeedStatus"
];
var PRICING_MAX_HISTORY_RANGE_DAYS = 90;
var DEFAULT_METAL = "XAU";
var DEFAULT_KARAT = "24";
function quoteTagId(args) {
  return `${args?.metal ?? DEFAULT_METAL}:${args?.karat ?? DEFAULT_KARAT}`;
}
function quoteParams(args) {
  const params = {};
  if (args?.metal !== void 0) params["metal"] = args.metal;
  if (args?.karat !== void 0) params["karat"] = args.karat;
  return params;
}
function feedScopeKey(args) {
  if (args?.scope === "PLATFORM") return "platform";
  return args?.tenantId ?? "self";
}
function feedScopeParams(args) {
  if (args?.scope === "PLATFORM") return { scope: "PLATFORM" };
  if (args?.tenantId != null) return { tenantId: args.tenantId };
  return {};
}
var pricingEndpoints = (build) => ({
  /**
   * The hot read path. §10.4 `livePricing`: 15 s cache, refetch on focus, and a
   * 30 s poll supplied by the calling hook
   * (`{ pollingInterval: CACHE_POLICY.livePricing.pollingIntervalMs }`) — RTK
   * Query has no endpoint-level poll. A stale gold rate must never be transacted
   * on; `beyondValidityWindow` on the response is the block signal.
   */
  getCurrentPrice: build.query({
    query: (args) => ({ url: "/pricing/current", params: quoteParams(args ?? void 0) }),
    keepUnusedDataFor: CACHE_POLICY.livePricing.keepUnusedDataFor,
    providesTags: (_result, _error, args) => [
      { type: "CurrentPrice", id: quoteTagId(args ?? void 0) }
    ]
  }),
  /**
   * Tick history over a required `[from, to]` instant range. A plain list, so no
   * cursor helper: the whole answer arrives in one response (server-capped at
   * 5000 rows) and the cache entry is keyed by the range itself.
   */
  getPriceHistory: build.query({
    query: (args) => ({
      url: "/pricing/history",
      params: { ...quoteParams(args), from: args.from, to: args.to }
    }),
    keepUnusedDataFor: CACHE_POLICY.report.keepUnusedDataFor,
    providesTags: (result) => providesList("PriceHistory", result)
  }),
  /**
   * One IST business day's closing price. Immutable once the day has closed, so
   * it gets the reference-data policy rather than the live one.
   */
  getClosingPrice: build.query({
    query: (args) => ({
      url: "/pricing/closing",
      params: { ...quoteParams(args), date: args.date }
    }),
    keepUnusedDataFor: CACHE_POLICY.reference.keepUnusedDataFor,
    providesTags: (_result, _error, args) => [
      { type: "ClosingPrice", id: `${quoteTagId(args)}:${args.date}` }
    ]
  }),
  /** Current manual-override status. `{ active: false }` is the normal answer, not an error. */
  getManualOverrideStatus: build.query({
    query: (args) => ({ url: "/pricing/manual-override", params: quoteParams(args ?? void 0) }),
    providesTags: (_result, _error, args) => [
      { type: "ManualOverride", id: quoteTagId(args ?? void 0) }
    ]
  }),
  /**
   * Sets the tenant's manual price override and returns the recomputed quote.
   *
   * Both mutations take a caller-minted `actionId`, and neither keys on anything
   * else. The backend `IdempotencyFilter` stores under `tenantId + key` alone —
   * no path, no body hash, 24-hour retention — and both of these are *repeatable*
   * against the same tenant: set → clear → set again is an ordinary Tuesday. A
   * key derived from the tenant (or a constant) would let the second, genuinely
   * different request inside 24 hours replay the first one's response and never
   * execute, leaving the tenant on a price nobody chose.
   *
   * Mint `actionId` once when the drawer opens — never per submit attempt, which
   * is indistinguishable from having no idempotency at all, and overriding the
   * live gold rate twice by accident mis-prices every transaction until someone
   * notices.
   *
   * `retryable` is safe precisely because that key is stable within one intent: a
   * 429/503 retry either lands the same override or replays its response.
   */
  setManualPrice: build.mutation({
    query: ({ actionId, ...body }) => ({
      url: "/pricing/manual-override",
      method: "POST",
      body,
      idempotencyKey: `pricing-override-set:${actionId}`,
      retryable: true
    }),
    invalidatesTags: () => [
      "ManualOverride",
      "CurrentPrice",
      "PriceHistory"
    ]
  }),
  /**
   * Clears the override, returning the tenant to the live feed. 204 No Content.
   *
   * Same `actionId` rule, for the sharper version of the same reason: a constant
   * key would let the stored 204 from the *previous* clear satisfy this one, so a
   * later override would silently stay active on live prices.
   */
  clearManualPrice: build.mutation({
    query: ({ actionId }) => ({
      url: "/pricing/manual-override",
      method: "DELETE",
      idempotencyKey: `pricing-override-clear:${actionId}`,
      retryable: true
    }),
    invalidatesTags: () => [
      "ManualOverride",
      "CurrentPrice",
      "PriceHistory"
    ]
  }),
  // ─── Feed configuration (admin console, design §12/§16) ─────────────────────
  /**
   * The addressed scope's rows plus the platform row, with the `effective`
   * marker and masked `apiKeyLast4`. Cached per scope key; no polling — the
   * *health* endpoint below is the one that polls.
   */
  getFeedConfig: build.query({
    query: (args) => ({
      url: "/pricing/feed-config",
      params: feedScopeParams(args ?? void 0)
    }),
    providesTags: (_r, _e, args) => [
      { type: "FeedConfig", id: feedScopeKey(args ?? void 0) }
    ],
    keepUnusedDataFor: 60
  }),
  /**
   * Configure (or re-configure) the addressed scope's MetalpriceAPI key. The
   * backend probes the submitted key before storing anything — a 422
   * (`FEED_PROBE_FAILED`) means nothing changed. `credentials` omitted means a
   * settings-only change against the stored key.
   *
   * Scope addressing is query-string only on this service (the body carries no
   * scope fields), and the idempotency key carries the scope key *and* the
   * drawer's draft id — the same entity-only-key replay trap as the gateway.
   */
  configureFeed: build.mutation({
    query: ({ draftId, scope, tenantId, ...body }) => ({
      url: "/pricing/feed-config",
      method: "POST",
      body,
      params: feedScopeParams({ scope, tenantId }),
      idempotencyKey: `pricing-feed-configure:${feedScopeKey({ scope, tenantId })}:${draftId}`
    }),
    // The bare types: a platform-row change alters what every tenant-scoped read
    // resolves as effective, and a new credential group changes whose health row
    // the status endpoint serves.
    invalidatesTags: ["FeedConfig", "FeedStatus"]
  }),
  /**
   * Deactivate one config row. 204. Not `@IdempotentRequest` server-side, so the
   * client skips the key. Deactivating the *platform* row stops the feed for
   * every non-overriding tenant — the console confirms before calling this.
   */
  deactivateFeedConfig: build.mutation({
    query: ({ id, ...scope }) => ({
      url: `/pricing/feed-config/${id}/deactivate`,
      method: "POST",
      params: feedScopeParams(scope),
      skipIdempotencyKey: true
    }),
    invalidatesTags: ["FeedConfig", "FeedStatus"]
  }),
  /**
   * On-demand probe of the addressed scope's *stored* key. Read-only at the
   * vendor, hence `skipIdempotencyKey`. Unlike the gateway probe this returns a
   * result body (`success: false` is a 200) plus the vendor's quota snapshot;
   * only "nothing stored" is a typed 422 (`FEED_NOT_CONFIGURED`).
   */
  probeFeed: build.mutation({
    query: (args) => ({
      url: "/pricing/feed-config/probe",
      method: "POST",
      params: feedScopeParams(args ?? void 0),
      skipIdempotencyKey: true
    })
  }),
  /**
   * The `price_feed_health` projection (design §10.4) — last success, error
   * classification and quota per credential group. The console polls this at
   * `CACHE_POLICY.feedHealth.pollingIntervalMs` (60 s), supplied by the calling
   * hook and gated to the visible page — RTK Query has no endpoint-level poll.
   */
  getFeedStatus: build.query({
    query: (args) => ({
      url: "/pricing/feed-status",
      params: feedScopeParams(args ?? void 0)
    }),
    providesTags: (_r, _e, args) => [
      { type: "FeedStatus", id: feedScopeKey(args ?? void 0) }
    ],
    keepUnusedDataFor: 30
  })
});

// src/index.ts
var pricingApi = createContextApi({
  context: "pricing",
  reducerPath: "pricingApi",
  tagTypes: PRICING_TAGS,
  endpoints: pricingEndpoints
});
var {
  useGetCurrentPriceQuery,
  useLazyGetCurrentPriceQuery,
  useGetPriceHistoryQuery,
  useGetClosingPriceQuery,
  useGetManualOverrideStatusQuery,
  useSetManualPriceMutation,
  useClearManualPriceMutation,
  useGetFeedConfigQuery,
  useLazyGetFeedConfigQuery,
  useConfigureFeedMutation,
  useDeactivateFeedConfigMutation,
  useProbeFeedMutation,
  useGetFeedStatusQuery
} = pricingApi;

export { PRICING_MAX_HISTORY_RANGE_DAYS, PRICING_TAGS, feedScopeKey, pricingApi, useClearManualPriceMutation, useConfigureFeedMutation, useDeactivateFeedConfigMutation, useGetClosingPriceQuery, useGetCurrentPriceQuery, useGetFeedConfigQuery, useGetFeedStatusQuery, useGetManualOverrideStatusQuery, useGetPriceHistoryQuery, useLazyGetCurrentPriceQuery, useLazyGetFeedConfigQuery, useProbeFeedMutation, useSetManualPriceMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map